/**
 * Generic seq-guarded upsert for `createRecordStore.ts` — split out to keep
 * the primary file under the project's line-count guidance.
 */

import type { SeqResult, StoredRecord } from './recordStoreTypes';

export interface UpsertEngineDeps<T> {
  getRecord: (id: string) => StoredRecord<T> | undefined;
  checkSeq: (storedSeq: number, eventSeq: number) => SeqResult;
  commit: (id: string, record: StoredRecord<T>) => void;
}

export interface UpsertEngine<T> {
  upsert(
    id: string,
    data: T,
    seq: number,
    opts?: { isComplete?: boolean; merge?: (existing: T, incoming: T) => T }
  ): SeqResult;
}

export function createUpsertEngine<T>(
  deps: UpsertEngineDeps<T>
): UpsertEngine<T> {
  function shallowMerge(existing: T, incoming: T): T {
    return { ...existing, ...incoming };
  }

  function upsert(
    id: string,
    data: T,
    seq: number,
    opts?: { isComplete?: boolean; merge?: (existing: T, incoming: T) => T }
  ): SeqResult {
    const existing = deps.getRecord(id);

    if (existing) {
      const seqResult = deps.checkSeq(existing.meta.lastSeq, seq);
      if (seqResult !== 'applied') {
        return seqResult;
      }
      const merge = opts?.merge ?? shallowMerge;
      const stored: StoredRecord<T> = {
        data: merge(existing.data, data),
        meta: {
          lastSeq: seq,
          fetchedAt: Date.now(),
          isComplete: opts?.isComplete ?? existing.meta.isComplete,
        },
      };
      deps.commit(id, stored);
      return 'applied';
    }

    const stored: StoredRecord<T> = {
      data,
      meta: {
        lastSeq: seq,
        fetchedAt: Date.now(),
        isComplete: opts?.isComplete ?? true,
      },
    };
    deps.commit(id, stored);
    return 'applied';
  }

  return { upsert };
}
